import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { resolveMediaUrl } from "../lib/media";
import { UploadSimple, Trash, ImageSquare } from "@phosphor-icons/react";

/**
 * Reusable image upload widget. Shows current image (if any) as a preview,
 * an Upload button, and a Remove button. Calls onChange(newInternalPath | "").
 *
 * Props:
 *  - value: current stored URL (internal like /api/uploads/<id> OR external http URL)
 *  - onChange(newUrl): called after upload succeeds (or with "" on remove)
 *  - kind: string tag to organise objects in storage (e.g. "player", "logo")
 *  - shape: "square" | "circle" (default "square")
 *  - size: pixel size (default 96)
 *  - testId: root data-testid
 */
export default function ImageUploader({ value, onChange, kind = "misc", shape = "square", size = 96, testId = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const rounded = shape === "circle" ? "rounded-full" : "rounded-md";
  const preview = resolveMediaUrl(value);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image (jpg / png / webp)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/uploads/image?kind=${encodeURIComponent(kind)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <div
        className={`bg-[#0a0a0a] border border-neutral-800 flex items-center justify-center overflow-hidden shrink-0 ${rounded}`}
        style={{ width: size, height: size }}
      >
        {preview ? (
          <img src={preview} alt="preview" className="w-full h-full object-cover" />
        ) : (
          <ImageSquare size={size * 0.4} weight="light" color="#525252" />
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload`}
          className="h-11 px-4 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60"
        >
          <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-remove`}
            className="h-9 px-3 rounded-md border border-neutral-800 hover:border-[#D90429] hover:text-[#D90429] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
          >
            <Trash size={12} weight="bold" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
